import clsx from 'clsx';
import ReactGA from 'react-ga4';
import PropTypes from 'prop-types';
import NextLink from 'next/link';
import LazyLoad from 'react-lazyload';
import { getEnv } from 'utils';
import { useRouter } from 'next/router';
import * as queryString from 'query-string';
import CustomButton from 'components/CustomButton';
import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, Grid } from '@mui/material';
import { PlanEnum, BillingIntervalEnum } from 'enums';
import { useStyles } from './styles';
import { Icons } from 'components/Icons';
import en from './priceLocale/en';
import br from './priceLocale/br';
import ind from './priceLocale/ind';
import { checkUserCountry } from 'utils/locale';
import ProfileService from 'services/ProfileService';
import { benefitsPricing } from 'utils/benefitsPricing';
import getCleverTap from 'utils/clevertap';
import { useAuthUser } from 'features/auth/AuthUserContext';

const Props = {
  title: PropTypes.string,
  bgColorProp: PropTypes.string,
};

const DefaultProps = {
  bgColorProp: '#FFF',
};

export default function PricingContentAlternative({ title, bgColorProp }) {
  const classes = useStyles();
  const router = useRouter();
  const { user, isAuthenticated } = useAuthUser();
  const [userCountry, setUserCountry] = useState('US');
  const [userLocale, setUserLocale] = useState(en);
  const promotion = userLocale.promotion;
  const applyClassPromotion = ['BR', 'IN'].includes(userCountry);
  const profileService = new ProfileService();
  const [userLastInvoice, setUserLastInvoice] = useState(null);
  const [itemsBenefits, setItemsBenefits] = useState(5);
  const clevertap = getCleverTap();

  async function getLastBillingInterval() {
    if (isAuthenticated) {
      const { data: lastInvoice } = await profileService.getLastInvoice();

      setUserLastInvoice(lastInvoice);
    }
  }

  function getUserCountry(userCountry) {
    let userLocale;

    switch (userCountry) {
      case 'BR':
        userLocale = br;
        break;
      case 'IN':
        userLocale = ind;
        break;
      default:
        userLocale = en;
    }

    setUserLocale(userLocale);
  }

  function makeCheckoutUrl(plan, billingInterval) {
    let params = queryString.stringify({ plan, billingInterval });

    if (userLocale.promotion) {
      params += `&promotion=${promotion}`;
    }

    if (isAuthenticated && !userLastInvoice) {
      params += '&trial=true';
    }

    if (isAuthenticated) {
      return `/pricing?${params}`;
    }

    return `/auth/login-register?${queryString.stringify({
      from_url: router.basePath + router.pathname,
      redirect: `${getEnv('MTG_URL_APP')}/pricing?${params}`,
    })}`;
  }

  function sendButtonClickTrackingEvents(plan, billingInterval) {
    let label;

    switch (plan) {
      case PlanEnum.TRIAL:
        label = 'beginner';
        break;
      case PlanEnum.STUDENT:
        label = 'junior';
        break;
      case PlanEnum.RESEARCHER:
        label = 'senior';
        break;
      case PlanEnum.LAB_RESEARCHER:
        label = 'proTeams';
        break;
      default:
        console.error(`Failed to resolve plan label for plan [${plan}]`);
    }

    ReactGA.event({
      category: 'pricing',
      action: 'card_clicked',
      label: 'Click button "Start Now" or "Subscribe" or "Subscribe Now" or "or Subscribe"',
      value: user.id,
    });

    clevertap.event.push('Clicks to view information detail', {
      Product: 'MTG',
      Email: user.email,
      URL: router.asPath,
      'Country code': user.country_code,
      Description: 'Clicked CTA Start Now',
      Locale: 'pricing',
    });
  }

  function bannerLocalPromotion() {
    if (userLocale === br || userLocale === ind) {
      const customerLocale = userLocale === br ? 'Brazilian' : 'Indian';

      return (
        <Box bgcolor="#5F29CC" textAlign="center" px={2} py={3} mb={6}>
          <Typography className={classes.textBannerLocale}>
            Only for <b>{customerLocale} customers</b> - enjoy a <span>50%</span>{' '}
            <span className="extra">
              <b>extra</b>
            </span>{' '}
            discount!
          </Typography>
        </Box>
      );
    }
  }

  useEffect(() => {
    async function countryUser() {
      const userCountry = await checkUserCountry.getUserCountry(user);
      setUserCountry(userCountry);
    }

    countryUser();
    getLastBillingInterval();
  }, [user, user?.country_code]);

  useEffect(() => {
    getUserCountry(userCountry);
  }, [userCountry]);

  return (
    <>
      <Box pt={5.625} bgcolor={bgColorProp}>
        <LazyLoad>
          <Container>
            <Typography className={classes.title}>
              <b>{title}</b>
            </Typography>
          </Container>
        </LazyLoad>
      </Box>

      {bannerLocalPromotion()}

      <Box bgcolor={bgColorProp} pb={8}>
        <LazyLoad>
          <Container>
            <Grid container={true} spacing={2} justifyContent="center">
              <Grid item={true} xs={12} md={3}>
                <Box
                  p={userLocale.locale === 'en' ? 4 : 3}
                  className={clsx(classes.priceCard, classes.priceCardActiveGray)}
                >
                  <Typography className={clsx(classes.cardTitle, classes.cardTitleEmphasis)}>
                    Monthly plan
                  </Typography>

                  <Typography className={classes.freeDays}>7 days free, then</Typography>

                  {['in', 'br'].includes(userLocale.locale) && (
                    <Box pt={2} mb={-3}>
                      <Typography className={classes.discountText}>
                        <del>
                          {userLocale.currency}
                          {userLocale.totalResearcherMonth}
                        </del>
                      </Typography>
                    </Box>
                  )}

                  <Box pt={3} textAlign="center">
                    <Box mb={5.25}>
                      <Typography className={classes.secondaryColor} component="span">
                        <Typography component="p" variant="h6" display="inline">
                          <b>{userLocale.currency}</b>
                        </Typography>{' '}
                        <Typography component="p" variant="h3" display="inline">
                          <b>{userLocale.researcherMonth}</b>
                        </Typography>{' '}
                        <Typography
                          display="inline"
                          className={clsx(classes.grayColor, classes.month)}
                        >
                          /Month
                        </Typography>
                      </Typography>

                      <Typography className={classes.textPrice}>Cancel anytime</Typography>
                    </Box>

                    <div className={applyClassPromotion ? 'ab-test-pricing' : ''}>
                      <NextLink
                        passHref={true}
                        href={makeCheckoutUrl(PlanEnum.RESEARCHER, BillingIntervalEnum.MONTH)}
                      >
                        <CustomButton
                          style={{ width: '100%' }}
                          variant="buttonSecondary"
                          onClick={() =>
                            sendButtonClickTrackingEvents(
                              PlanEnum.RESEARCHER,
                              BillingIntervalEnum.MONTH,
                            )
                          }
                        >
                          Get started
                        </CustomButton>
                      </NextLink>
                    </div>
                  </Box>
                </Box>
              </Grid>

              <Grid item={true} xs={12} md={3}>
                <Box
                  p={userLocale.locale === 'en' ? 4 : 3}
                  className={clsx(classes.priceCard, classes.priceCardActiveSecondary)}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography className={clsx(classes.cardTitle, classes.cardTitleEmphasis)}>
                      Yearly plan
                    </Typography>

                    <span className={classes.tagMostPopular}>Save 38%</span>
                  </Box>

                  <Typography className={classes.freeDays}>7 days free, then</Typography>

                  {['in', 'br'].includes(userLocale.locale) && (
                    <Box pt={2} mb={-3}>
                      <Typography className={classes.discountText}>
                        <del>
                          {userLocale.currency}
                          {userLocale.totalResearcherYear}
                        </del>
                      </Typography>
                    </Box>
                  )}

                  <Box pt={3} textAlign="center">
                    <Box mb={3}>
                      <Typography className={classes.secondaryColor} component="span">
                        <Typography component="p" variant="h6" display="inline">
                          <b>{userLocale.currency}</b>
                        </Typography>{' '}
                        <Typography component="p" variant="h3" display="inline">
                          <b>{userLocale.researcherYear}</b>
                        </Typography>{' '}
                        <Typography
                          display="inline"
                          className={clsx(classes.grayColor, classes.month)}
                        >
                          /Month
                        </Typography>
                      </Typography>

                      <Typography className={classes.textPrice}>
                        {userLocale.currency} {userLocale.totalYear} charged yearly
                      </Typography>

                      <Typography className={classes.textPrice}>Cancel anytime</Typography>
                    </Box>

                    <div className={applyClassPromotion ? 'ab-test-pricing' : ''}>
                      <NextLink
                        passHref={true}
                        href={makeCheckoutUrl(PlanEnum.RESEARCHER, BillingIntervalEnum.YEAR)}
                      >
                        <CustomButton
                          style={{ width: '100%' }}
                          variant="buttonSecondary"
                          onClick={() =>
                            sendButtonClickTrackingEvents(
                              PlanEnum.RESEARCHER,
                              BillingIntervalEnum.YEAR,
                            )
                          }
                        >
                          Get started
                        </CustomButton>
                      </NextLink>
                    </div>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Box display="flex" justifyContent="center" width="100%">
              <Box
                display="flex"
                alignItems="center"
                pt={4}
                justifyContent="space-between"
                width="600px"
              >
                <div className={classes.line} />
                <Typography style={{ color: '#D6246E', fontSize: 18, padding: '6px' }}>
                  <b>What you’ll get</b>
                </Typography>
                <div className={classes.line} />
              </Box>
            </Box>

            <Box display="flex" justifyContent="center">
              <Box className={classes.benefitsList}>
                {benefitsPricing.slice(0, itemsBenefits).map((benefit) => (
                  <div key={benefit.id}>
                    <Icons.done />
                    <p>{benefit.text}</p>
                  </div>
                ))}

                <Box pt={2}>
                  {itemsBenefits === 5 ? (
                    <Typography
                      className={classes.allFeatures}
                      component="p"
                      onClick={() => setItemsBenefits(17)}
                    >
                      See All Features
                    </Typography>
                  ) : (
                    <Typography
                      className={classes.allFeatures}
                      component="p"
                      onClick={() => setItemsBenefits(5)}
                    >
                      See less features
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Container>
        </LazyLoad>
      </Box>
    </>
  );
}

PricingContentAlternative.propTypes = Props;
PricingContentAlternative.defaultProps = DefaultProps;
